import {
  HOME_MAP_ALWAYS_VISIBLE_CLUSTER_ZOOM,
  HOME_MAP_CLUSTER_BREAK_ZOOM,
  buildLocationClusterKey,
} from "./homeMapV2Utils";

const DEFAULT_MAP_ZOOM = 18;
const CLUSTER_ICON_SIZE = 54;
const MEDIUM_GRID_CLUSTER_ZOOM = 13;

function latLngToWorldPixel(position, zoom) {
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const scale = 256 * Math.pow(2, Number.isFinite(zoom) ? zoom : DEFAULT_MAP_ZOOM);
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const clampedSin = Math.min(Math.max(sinLat, -0.9999), 0.9999);

  return {
    x: ((lng + 180) / 360) * scale,
    y:
      (0.5 - Math.log((1 + clampedSin) / (1 - clampedSin)) / (4 * Math.PI)) *
      scale,
  };
}

function getClusterFootprintScale(zoom) {
  const normalizedZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : DEFAULT_MAP_ZOOM;
  if (normalizedZoom >= 17) return 1.65;
  if (normalizedZoom >= 16) return 1.9;
  if (normalizedZoom >= 15) return 2.2;
  if (normalizedZoom >= 14) return 1.9;
  if (normalizedZoom >= 13) return 2.4;
  if (normalizedZoom >= 12) return 3.1;
  if (normalizedZoom >= 10) return 4.1;
  if (normalizedZoom >= 8) return 5.2;
  return 6.4;
}

function getItemBounds(item, zoom) {
  const position =
    item?.kind === "cluster" ? item?.position : item?.marker?.position || item?.position;
  const worldPixel = latLngToWorldPixel(position, zoom);
  if (!worldPixel) return null;
  const baseWidth =
    item?.kind === "cluster"
      ? CLUSTER_ICON_SIZE
      : Math.max(1, Number(item?.marker?.width || item?.width) || 60);
  const baseHeight =
    item?.kind === "cluster"
      ? CLUSTER_ICON_SIZE
      : Math.max(1, Number(item?.marker?.height || item?.height) || baseWidth);
  const scale = getClusterFootprintScale(zoom);
  const width = Math.max(1, baseWidth * scale);
  const height = Math.max(1, baseHeight * scale);
  return {
    left: worldPixel.x - width / 2,
    right: worldPixel.x + width / 2,
    top: worldPixel.y - height,
    bottom: worldPixel.y,
  };
}

function boundsOverlap(a, b) {
  if (!a || !b) return false;
  return !(
    a.right <= b.left ||
    b.right <= a.left ||
    a.bottom <= b.top ||
    b.bottom <= a.top
  );
}

function averagePosition(markers) {
  const valid = markers.filter(
    (marker) => Number.isFinite(marker?.position?.lat) && Number.isFinite(marker?.position?.lng)
  );
  if (!valid.length) return null;
  const sums = valid.reduce(
    (acc, marker) => ({
      lat: acc.lat + marker.position.lat,
      lng: acc.lng + marker.position.lng,
    }),
    { lat: 0, lng: 0 }
  );
  return {
    lat: sums.lat / valid.length,
    lng: sums.lng / valid.length,
  };
}

function toMarkerItem(marker) {
  return {
    kind: "marker",
    key: `marker:${marker.id}`,
    marker,
    position: marker.position,
  };
}

function toClusterItem(markers) {
  const sortedIds = markers.map((marker) => marker.id).sort().join(":");
  return {
    kind: "cluster",
    key: `cluster:${sortedIds}`,
    markers,
    count: markers.length,
    position: averagePosition(markers) || markers[0]?.position || null,
  };
}

function getItemMarkers(item) {
  if (item?.kind === "cluster") {
    return Array.isArray(item?.markers) ? item.markers : [];
  }
  return item?.marker ? [item.marker] : [];
}

function buildExactLocationGroups(markers = []) {
  const byKey = new Map();
  (Array.isArray(markers) ? markers : []).forEach((marker) => {
    const key = String(marker?.locationClusterKey || buildLocationClusterKey(marker?.position) || "").trim();
    if (!key) {
      byKey.set(`marker:${marker?.id || Math.random()}`, [marker]);
      return;
    }
    const existing = byKey.get(key) || [];
    existing.push(marker);
    byKey.set(key, existing);
  });
  return Array.from(byKey.values());
}

function getGridCellSize(zoom) {
  const normalizedZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : DEFAULT_MAP_ZOOM;
  if (normalizedZoom >= 15) return 104;
  if (normalizedZoom >= 14) return 124;
  if (normalizedZoom >= 13) return 148;
  if (normalizedZoom >= 12) return 180;
  if (normalizedZoom >= 11) return 212;
  return 248;
}

function buildGridClusterItems(markers = [], zoom = DEFAULT_MAP_ZOOM) {
  const cellSize = getGridCellSize(zoom);
  const buckets = new Map();
  const singles = [];

  (Array.isArray(markers) ? markers : []).forEach((marker) => {
    const pixel = latLngToWorldPixel(marker?.position, zoom);
    if (!pixel) {
      singles.push(toMarkerItem(marker));
      return;
    }

    const cellX = Math.floor(pixel.x / cellSize);
    const cellY = Math.floor(pixel.y / cellSize);
    const key = `${cellX}:${cellY}`;
    const existing = buckets.get(key) || [];
    existing.push(marker);
    buckets.set(key, existing);
  });

  const clustered = Array.from(buckets.values()).map((group) =>
    group.length === 1 ? toMarkerItem(group[0]) : toClusterItem(group)
  );

  return [...singles, ...clustered];
}

function clusterOverlapItems(items = [], zoom = DEFAULT_MAP_ZOOM) {
  const prepared = (Array.isArray(items) ? items : []).map((item) => ({
    item,
    bounds: getItemBounds(item, zoom),
  }));
  const visited = new Set();
  const nextItems = [];
  let mergedAny = false;

  for (let i = 0; i < prepared.length; i += 1) {
    const seedItem = prepared[i]?.item;
    if (!seedItem?.key || visited.has(seedItem.key)) continue;

    const queue = [prepared[i]];
    const group = [];
    visited.add(seedItem.key);

    while (queue.length) {
      const current = queue.shift();
      group.push(current.item);

      for (let j = 0; j < prepared.length; j += 1) {
        const candidate = prepared[j];
        if (!candidate?.item?.key || visited.has(candidate.item.key)) continue;
        if (!boundsOverlap(current.bounds, candidate.bounds)) continue;
        visited.add(candidate.item.key);
        queue.push(candidate);
      }
    }

    if (group.length === 1) {
      nextItems.push(group[0]);
      continue;
    }

    mergedAny = true;
    const mergedMarkers = group.flatMap(getItemMarkers);
    nextItems.push(toClusterItem(mergedMarkers));
  }

  return {
    items: nextItems,
    mergedAny,
  };
}

function mergeOverlappingDisplayItems(items = [], zoom = DEFAULT_MAP_ZOOM, maxIterations = 8) {
  let nextItems = Array.isArray(items) ? items : [];
  let iteration = 0;

  while (iteration < maxIterations) {
    const { items: mergedItems, mergedAny } = clusterOverlapItems(nextItems, zoom);
    nextItems = mergedItems;
    if (!mergedAny) break;
    iteration += 1;
  }

  return nextItems;
}

export default function buildHomeMapDisplayItemsV2(markers = [], zoom = DEFAULT_MAP_ZOOM) {
  const exactLocationGroups = buildExactLocationGroups(markers);
  const immediateDisplayItems = [];
  const uniqueMarkers = [];

  exactLocationGroups.forEach((group) => {
    if (group.length > 1) {
      immediateDisplayItems.push(toClusterItem(group));
      return;
    }
    uniqueMarkers.push(group[0]);
  });

  if (Number(zoom) >= HOME_MAP_CLUSTER_BREAK_ZOOM) {
    const protectedExactOnlyMarkers = [];
    const clusterableMarkers = uniqueMarkers;
    const { items: clusteredItems } = clusterOverlapItems(
      clusterableMarkers.map(toMarkerItem),
      zoom
    );

    const displayItems = mergeOverlappingDisplayItems(
      [
        ...immediateDisplayItems,
        ...protectedExactOnlyMarkers.map(toMarkerItem),
        ...clusteredItems,
      ],
      zoom
    );
    return displayItems;
  }

  if (Number(zoom) >= HOME_MAP_ALWAYS_VISIBLE_CLUSTER_ZOOM) {
    const protectedExactOnlyMarkers = [];
    const clusterableMarkers = uniqueMarkers;
    let clusteredItems = clusterableMarkers.map(toMarkerItem);
    let iteration = 0;

    while (iteration < 6) {
      const { items, mergedAny } = clusterOverlapItems(clusteredItems, zoom);
      clusteredItems = items;
      if (!mergedAny) break;
      iteration += 1;
    }

    const displayItems = mergeOverlappingDisplayItems(
      [
        ...immediateDisplayItems,
        ...protectedExactOnlyMarkers.map(toMarkerItem),
        ...clusteredItems,
      ],
      zoom
    );
    return displayItems;
  }

  if (Number(zoom) >= MEDIUM_GRID_CLUSTER_ZOOM) {
    const displayItems = mergeOverlappingDisplayItems(
      [...immediateDisplayItems, ...buildGridClusterItems(uniqueMarkers, zoom)],
      zoom
    );
    return displayItems;
  }

  let clusteredItems = buildGridClusterItems(uniqueMarkers, zoom);
  let iteration = 0;

  while (iteration < 4) {
    const { items, mergedAny } = clusterOverlapItems(clusteredItems, zoom);
    clusteredItems = items;
    if (!mergedAny) break;
    iteration += 1;
  }

  const displayItems = mergeOverlappingDisplayItems(
    [...immediateDisplayItems, ...clusteredItems],
    zoom
  );
  return displayItems;
}
